import { Dialog } from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import MyDialogContent from "@/components/MyDialogContent";
import { formatDateTime12 } from "@/utils/helpers";
import type { NursingNoteV2 } from "../types";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  note: NursingNoteV2 | null;
};

export function NursingNoteHistoryDialog({ open, onOpenChange, note }: Props) {
  const history = Array.isArray((note as Record<string, unknown> | null)?.editHistory)
    ? ((note as Record<string, unknown>).editHistory as Record<string, unknown>[])
    : [];

  return (
    <Dialog open={open && !!note} onOpenChange={onOpenChange}>
      <MyDialogContent className="max-w-4xl" heading="Nursing Note History">
        {history.length > 0 ? (
          <div className="max-h-[60vh] overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Date &amp; Time</TableHead>
                  <TableHead>Author</TableHead>
                  <TableHead>Program</TableHead>
                  <TableHead>Care Type</TableHead>
                  <TableHead>Duration (min)</TableHead>
                  <TableHead>Content</TableHead>
                  <TableHead>Changes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((h, index) => (
                  <TableRow key={String(h._id ?? index)}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      {h.editedAt || h.dateTime
                        ? formatDateTime12(String(h.editedAt || h.dateTime))
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {String(
                        (h.editedBy as { name?: string } | undefined)?.name ||
                          h.authorName ||
                          "-",
                      )}
                    </TableCell>
                    <TableCell>
                      {String(h.program || (note as Record<string, unknown>)?.program || "-")}
                    </TableCell>
                    <TableCell>
                      {String(h.careType || (note as Record<string, unknown>)?.careType || "-")}
                    </TableCell>
                    <TableCell>
                      {String(h.duration ?? (note as Record<string, unknown>)?.duration ?? "-")}
                    </TableCell>
                    <TableCell className="max-w-xs whitespace-pre-wrap wrap-break-word">
                      {String(h.content || (note as Record<string, unknown>)?.content || "-")}
                    </TableCell>
                    <TableCell className="max-w-xs whitespace-pre-wrap wrap-break-word text-xs">
                      {Array.isArray(h.changes) && h.changes.length > 0
                        ? (h.changes as { field?: string; previous?: unknown; current?: unknown }[])
                            .map((c) => `${c.field}: ${c.previous ?? ""} → ${c.current ?? ""}`)
                            .join("\n")
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            No history available for this nursing note.
          </p>
        )}
      </MyDialogContent>
    </Dialog>
  );
}
